import React, { useState, useEffect } from 'react';
import { Clock, AlertTriangle, Shield, Users, Save, Timer } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const DAYS = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];

export default function SLAPage() {
  const [configs, setConfigs] = useState([]);
  const [workingHours, setWorkingHours] = useState([]);
  const [slaTickets, setSlaTickets] = useState([]);
  const [staff, setStaff] = useState([]);
  const [activeTab, setActiveTab] = useState('status');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [cfgRes, whRes, slaRes, staffRes] = await Promise.all([
        api.get('/sla/configs'),
        api.get('/sla/working-hours'),
        api.get('/sla/status'),
        api.get('/users')
      ]);
      setConfigs(cfgRes.data);
      setWorkingHours(whRes.data);
      setSlaTickets(slaRes.data);
      setStaff(staffRes.data.filter(u => u.Role === 'admin' || u.Role === 'staff'));
    } catch (err) {}
  };

  const updateConfig = async (id, field, value) => {
    const cfg = configs.find(c => c.Id === id);
    if (!cfg) return;
    const updated = { ...cfg, [field]: value };
    try {
      await api.put(`/sla/configs/${id}`, {
        firstResponseMinutes: updated.FirstResponseMinutes,
        resolutionMinutes: updated.ResolutionMinutes,
        escalateToUserId: updated.EscalateToUserId
      });
      setConfigs(prev => prev.map(c => c.Id === id ? { ...c, [field]: value } : c));
      toast.success('SLA güncellendi');
    } catch (err) { toast.error('Güncellenemedi'); }
  };

  const saveWorkingHours = async () => {
    try {
      await api.put('/sla/working-hours', { hours: workingHours });
      toast.success('Çalışma saatleri güncellendi');
    } catch (err) { toast.error('Güncellenemedi'); }
  };

  const formatMinutes = (mins) => {
    if (mins >= 1440) return `${Math.round(mins / 1440)} gün`;
    if (mins >= 60) return `${Math.round(mins / 60)} saat`;
    return `${mins} dk`;
  };

  const getSLAStatus = (ticket) => {
    if (!ticket.ResolutionMinutes) return { status: 'unknown', label: 'Tanımsız', color: 'slate' };
    const elapsed = ticket.ElapsedMinutes;
    const limit = ticket.ResolutionMinutes;
    const pct = (elapsed / limit) * 100;
    if (pct >= 100) return { status: 'breached', label: 'SLA Aşıldı', color: 'red', pct };
    if (pct >= 80) return { status: 'at-risk', label: 'Risk Altında', color: 'amber', pct };
    return { status: 'ok', label: 'Normal', color: 'emerald', pct };
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">SLA Yönetimi</h1>
          <p className="text-sm text-slate-500 mt-1">Hizmet seviyesi anlaşması ve çalışma saatleri</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-1">
        {[
          { key: 'status', label: 'SLA Durumu', icon: AlertTriangle },
          { key: 'configs', label: 'SLA Kuralları', icon: Shield },
          { key: 'hours', label: 'Çalışma Saatleri', icon: Clock },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2.5 rounded-t-lg text-sm font-medium transition-all ${
              activeTab === tab.key ? 'bg-white text-blue-700 border border-b-0 border-slate-200 -mb-[1px]' : 'text-slate-500 hover:text-slate-700'
            }`}>
            <tab.icon size={16} /> {tab.label}
          </button>
        ))}
      </div>

      {/* SLA Status */}
      {activeTab === 'status' && (
        <div className="space-y-4">
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-red-50 border border-red-100 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-red-700">{slaTickets.filter(t => getSLAStatus(t).status === 'breached').length}</p>
              <p className="text-xs text-red-600 font-medium">SLA Aşılmış</p>
            </div>
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-amber-700">{slaTickets.filter(t => getSLAStatus(t).status === 'at-risk').length}</p>
              <p className="text-xs text-amber-600 font-medium">Risk Altında</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-emerald-700">{slaTickets.filter(t => getSLAStatus(t).status === 'ok').length}</p>
              <p className="text-xs text-emerald-600 font-medium">Normal</p>
            </div>
          </div>

          {/* Ticket List */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Ticket</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Öncelik</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Durum</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Geçen Süre</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">SLA Limiti</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">SLA</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500">Atanan</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {slaTickets.map(t => {
                    const sla = getSLAStatus(t);
                    return (
                      <tr key={t.Id} className={`hover:bg-slate-50 ${sla.status === 'breached' ? 'bg-red-50/50' : ''}`}>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs text-slate-400">#{t.Id}</span>
                          <p className="text-sm font-medium text-slate-800 truncate max-w-[200px]">{t.Title}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                            t.Priority === 'Acil' ? 'bg-red-100 text-red-700' : t.Priority === 'Yüksek' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'
                          }`}>{t.Priority}</span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{t.Status}</td>
                        <td className="px-4 py-3 text-xs font-medium text-slate-700">{formatMinutes(t.ElapsedMinutes)}</td>
                        <td className="px-4 py-3 text-xs text-slate-500">{t.ResolutionMinutes ? formatMinutes(t.ResolutionMinutes) : '-'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-16 bg-slate-100 rounded-full h-2">
                              <div className={`h-2 rounded-full bg-${sla.color}-500`} style={{ width: `${Math.min(sla.pct || 0, 100)}%` }} />
                            </div>
                            <span className={`text-xs font-bold text-${sla.color}-600`}>{sla.label}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{t.AssignedToName || '-'}</td>
                      </tr>
                    );
                  })}
                  {slaTickets.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-slate-400">Açık ticket yok</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* SLA Configs */}
      {activeTab === 'configs' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <h3 className="text-base font-semibold text-slate-800 mb-4">Önceliğe Göre SLA Süreleri</h3>
          <div className="space-y-4">
            {configs.map(cfg => (
              <div key={cfg.Id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="w-24">
                  <span className={`text-sm font-bold ${
                    cfg.Priority === 'Acil' ? 'text-red-600' : cfg.Priority === 'Yüksek' ? 'text-orange-600' : cfg.Priority === 'Normal' ? 'text-blue-600' : 'text-slate-600'
                  }`}>{cfg.Priority}</span>
                </div>
                <div className="flex-1 grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">İlk Yanıt (dakika)</label>
                    <input type="number" value={cfg.FirstResponseMinutes}
                      onChange={e => setConfigs(prev => prev.map(c => c.Id === cfg.Id ? { ...c, FirstResponseMinutes: parseInt(e.target.value) } : c))}
                      onBlur={e => updateConfig(cfg.Id, 'FirstResponseMinutes', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Çözüm Süresi (dakika)</label>
                    <input type="number" value={cfg.ResolutionMinutes}
                      onChange={e => setConfigs(prev => prev.map(c => c.Id === cfg.Id ? { ...c, ResolutionMinutes: parseInt(e.target.value) } : c))}
                      onBlur={e => updateConfig(cfg.Id, 'ResolutionMinutes', parseInt(e.target.value))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  </div>
                  <div>
                    <label className="text-xs text-slate-500 block mb-1">Eskalasyon Personeli</label>
                    <select value={cfg.EscalateToUserId || ''}
                      onChange={e => updateConfig(cfg.Id, 'EscalateToUserId', e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                      <option value="">Seçiniz</option>
                      {staff.map(s => <option key={s.Id} value={s.Id}>{s.FullName}</option>)}
                    </select>
                  </div>
                </div>
                <div className="text-right text-xs text-slate-400">
                  <p>İlk Yanıt: {formatMinutes(cfg.FirstResponseMinutes)}</p>
                  <p>Çözüm: {formatMinutes(cfg.ResolutionMinutes)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Working Hours */}
      {activeTab === 'hours' && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
          <h3 className="text-base font-semibold text-slate-800 mb-4">Çalışma Saatleri</h3>
          <p className="text-sm text-slate-500 mb-4">SLA hesaplaması bu saatler dahilinde yapılır</p>
          <div className="space-y-3">
            {workingHours.map((wh, idx) => (
              <div key={wh.Id} className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl">
                <div className="w-28">
                  <span className="text-sm font-medium text-slate-700">{DAYS[wh.DayOfWeek]}</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={wh.IsWorkDay}
                    onChange={e => setWorkingHours(prev => prev.map((h, i) => i === idx ? { ...h, IsWorkDay: e.target.checked } : h))}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600" />
                  <span className="text-sm text-slate-600">Çalışma Günü</span>
                </label>
                {wh.IsWorkDay && (
                  <>
                    <input type="time" value={wh.StartTime}
                      onChange={e => setWorkingHours(prev => prev.map((h, i) => i === idx ? { ...h, StartTime: e.target.value } : h))}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                    <span className="text-slate-400">-</span>
                    <input type="time" value={wh.EndTime}
                      onChange={e => setWorkingHours(prev => prev.map((h, i) => i === idx ? { ...h, EndTime: e.target.value } : h))}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
                  </>
                )}
                {!wh.IsWorkDay && <span className="text-xs text-slate-400 italic">Tatil</span>}
              </div>
            ))}
          </div>
          <button onClick={saveWorkingHours}
            className="mt-4 flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700">
            <Save size={18} /> Kaydet
          </button>
        </div>
      )}
    </div>
  );
}
